import { findOrganizationTaskTemplateIncompatibility } from "@common/helpers/organizationTaskTemplateCompatibility";
import { accrualReviewCloseTemplateViewConfigSchema } from "@common/schemas/close-template-task-view-config.schema";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import type { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { TaskType } from "@common/types/task";
import { toLocalTask } from "../OrganizationTaskTemplates/organizationTaskTemplateMappers";
import {
    findAccrualReviewPresetForBucket,
    type TaskTypeOption,
} from "./closeTemplateTasksDrawer.constants";
import type { LocalTask } from "./closeTemplateTasksDrawer.types";
import {
    normalizeTransactionReviewViewConfigForBasis,
    readTransactionsViewTypeFromViewConfig,
    type TransactionsViewType,
} from "./closeTemplateTaskViewConfig";

const ORG_TASK_TEMPLATE_VALUE_PREFIX = "__org_task_template__";

export type OrgTaskTemplatePickerValue =
    `${typeof ORG_TASK_TEMPLATE_VALUE_PREFIX}${string}`;

export const toOrgTaskTemplatePickerValue = (
    templateId: string,
): OrgTaskTemplatePickerValue =>
    `${ORG_TASK_TEMPLATE_VALUE_PREFIX}${templateId}`;

export const isOrgTaskTemplatePickerValue = (
    value: string,
): value is OrgTaskTemplatePickerValue =>
    value.startsWith(ORG_TASK_TEMPLATE_VALUE_PREFIX);

export const parseOrgTaskTemplateIdFromPickerValue = (
    value: OrgTaskTemplatePickerValue,
): string => value.slice(ORG_TASK_TEMPLATE_VALUE_PREFIX.length);

/**
 * Org task templates a service template may use: org-wide templates (no
 * workspace) are usable everywhere; client-scoped templates only for the same
 * client. An org-wide service template (no workspace) only sees org-wide ones.
 * Accrual-review templates are hidden on modified-cash-ledger service
 * templates, matching the built-in accrual-review workflows.
 */
export const filterOrgTaskTemplatesForServiceTemplate = ({
    templates,
    workspaceId,
    basis,
}: {
    templates: readonly OrganizationTaskTemplate[];
    workspaceId: string | null;
    basis: CloseTemplateBasis | null;
}): OrganizationTaskTemplate[] =>
    templates.filter(
        (template) =>
            findOrganizationTaskTemplateIncompatibility({
                template,
                workspaceId,
                basis,
            }) === null,
    );

export interface OrgTaskTemplateFormDefaults extends Omit<
    LocalTask,
    | "localId"
    | "persistedId"
    | "isDeleted"
    | "order"
    | "taskType"
    | "sourceTemplateId"
    | "sourceTemplateVersion"
    | "blockedByLocalIds"
    | "relatedLocalIds"
> {
    taskType: TaskType;
    taskTypeOption: TaskTypeOption;
    sourceTemplateId: string;
}

const toTaskTypeOption = (
    taskType: TaskType,
    viewConfig: LocalTask["viewConfig"],
): TaskTypeOption => {
    if (taskType !== TaskType.ACCRUAL_REVIEW) {
        return taskType;
    }
    const parsed =
        accrualReviewCloseTemplateViewConfigSchema.safeParse(viewConfig);
    const bucket = parsed.success
        ? parsed.data.filters.accrualSuggestionBuckets?.[0]
        : undefined;
    return (bucket && findAccrualReviewPresetForBucket(bucket)) ?? taskType;
};

export const toFormDefaultsFromOrgTaskTemplate = ({
    template,
    basis,
}: {
    template: OrganizationTaskTemplate;
    basis: CloseTemplateBasis | null;
}): OrgTaskTemplateFormDefaults => {
    const {
        localId: _localId,
        persistedId: _persistedId,
        isDeleted: _isDeleted,
        order: _order,
        sourceTemplateVersion: _sourceTemplateVersion,
        blockedByLocalIds: _blockedByLocalIds,
        relatedLocalIds: _relatedLocalIds,
        taskType: storedTaskType,
        viewConfig: storedViewConfig,
        ...fields
    } = toLocalTask(template);
    const taskType = storedTaskType ?? TaskType.QUESTION;
    return {
        ...fields,
        sourceTemplateId: template.id,
        taskType,
        taskTypeOption: toTaskTypeOption(taskType, storedViewConfig),
        viewConfig:
            taskType === TaskType.TRANSACTION_REVIEW
                ? normalizeTransactionReviewViewConfigForBasis(
                      storedViewConfig,
                      basis,
                  )
                : storedViewConfig,
    };
};

export interface OrgTaskTemplateDrawerDefaults extends Pick<
    OrgTaskTemplateFormDefaults,
    "taskType" | "taskTypeOption" | "sourceTemplateId" | "viewConfig"
> {
    formValues: Omit<
        OrgTaskTemplateFormDefaults,
        "taskType" | "taskTypeOption" | "sourceTemplateId" | "viewConfig"
    >;
    transactionsViewType: TransactionsViewType;
}

export const toDrawerDefaultsFromOrgTaskTemplate = ({
    template,
    basis,
}: {
    template: OrganizationTaskTemplate;
    basis: CloseTemplateBasis | null;
}): OrgTaskTemplateDrawerDefaults => {
    const {
        taskType,
        taskTypeOption,
        sourceTemplateId,
        viewConfig,
        ...formValues
    } = toFormDefaultsFromOrgTaskTemplate({ template, basis });
    return {
        formValues,
        taskType,
        taskTypeOption,
        sourceTemplateId,
        viewConfig,
        transactionsViewType:
            readTransactionsViewTypeFromViewConfig(viewConfig),
    };
};
